import React, { useMemo, useState } from 'react';
import './PlayerGroupSection.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import CustomDropdown from '../input/CustomDropdown';
import CustomTogglebox from '../input/CustomTogglebox';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import { ParticipantGroupChip } from '../participant-group/ParticipantGroupChips';
import ImpactPreview, { futureParticipationsLine } from './ImpactPreview';
import usePreviewRequest from './usePreviewRequest';
import apiErrorText from './apiErrorText';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import {
  colorVar, MEMBERSHIP_ORIGIN_LABEL, normalizeColorKey, normalizeMembershipOrigin, normalizeParticipantGroup,
  VALIDITY_MODE_LABEL,
} from '../../util/participantGroups';
import {
  addDays, coversDate, endOfYear, formatDate, isoDate, rangeLabel, rangesOverlap, reachesDate, todayIso, yearOf,
} from './membershipDates';

const POPUP = {
  switch: 'editplayerswitchgroup',
  add: 'editplayeraddmembership',
  edit: 'editplayereditmembership',
  extend: 'editplayerextendmembership',
  end: 'editplayerendmembership',
  remove: 'editplayerremovemembership',
};

const PREVIEW_URL = '/ParticipantGroupMembership/Preview';
const NOTE_MAX_LENGTH = 500;

// Valid-to suggestion by the group's validity mode: calendar-year groups end on 31.12., others open-ended.
const defaultValidTo = (group, fromIso) => (group?.validityMode === 'CalendarYear' ? endOfYear(yearOf(fromIso)) : '');

// "Verlängern" moves the end to 31.12. of the following year (ParticipantGroupMembershipService.ExtendAsync).
const extendTarget = (m) => endOfYear(yearOf(m.validTo) + 1);

const byValidFromAsc = (a, b) => (isoDate(a.validFrom) < isoDate(b.validFrom) ? -1 : 1);

// Memberships oldest first, with the gaps in between (and after the last one) as "Gast" entries.
function buildTimeline(memberships) {
  const asc = [...memberships].sort(byValidFromAsc);
  const items = [];
  asc.forEach((m, i) => {
    items.push({ kind: 'membership', key: `m${m.id}`, membership: m });
    if (!m.validTo) return;
    const gapFrom = addDays(m.validTo, 1);
    const next = asc[i + 1];
    if (!next) {
      items.push({ kind: 'guest', key: `g${m.id}`, from: gapFrom, to: null });
      return;
    }
    const gapTo = addDays(next.validFrom, -1);
    if (gapFrom <= gapTo) items.push({ kind: 'guest', key: `g${m.id}`, from: gapFrom, to: gapTo });
  });
  return items;
}

// Does the action change anything for events from today on? Otherwise no recalculation toggle/preview.
function isFutureRelevant(dialog, today) {
  const { kind, membership: m, form: f } = dialog;
  switch (kind) {
    case 'switch': return true;
    case 'add': return reachesDate(f.validTo || null, today);
    case 'edit': return reachesDate(m.validTo, today) || reachesDate(f.validTo || null, today);
    case 'extend': return reachesDate(extendTarget(m), today);
    case 'end':
    case 'remove': return reachesDate(m.validTo, today);
    default: return false;
  }
}

/**
 * Section "Gruppe" of the player page (UX decision 8): card of the current group with the quick action
 * "Gruppe wechseln", a timeline of all memberships (oldest first, gaps shown as "Gast") with the actions
 * per entry, and "Mitgliedschaft hinzufügen". Every change runs through a popup with the impact preview of
 * /ParticipantGroupMembership/Preview (requested only while the popup is open); afterwards the parent
 * reloads just the player's group history (onChanged).
 */
function PlayerGroupSection({ player, history, historyFailed, onChanged, groupMailsEnabled, onGroupMailsChange }) {
  const { active, byId, guest } = useParticipantGroups();
  const { openPopup, closePopup, isPopupOpen, popupData } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  // { kind, membership, form } — kept after closing so the popup keeps its content during the exit animation.
  const [dialog, setDialog] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [saving, setSaving] = useState(false);

  const today = todayIso();
  const playerName = `${player.firstName ?? ''} ${player.lastName ?? ''}`.trim();
  const memberships = useMemo(() => history?.memberships ?? [], [history]);
  const selectable = useMemo(() => active.filter(g => !g.isGuest), [active]);

  const groupOf = (id, fallbackName) => byId.get(Number(id))
    ?? { id: Number(id), name: fallbackName ?? '?', pluralName: fallbackName ?? '?', colorKey: 'neutral', validityMode: 'OpenEnded', isGuest: false, isArchived: false };

  const currentMembership = memberships.find(m => m.isCurrent) ?? null;
  const currentGroup = history?.currentGroup
    ? (byId.get(Number(history.currentGroup.id)) ?? normalizeParticipantGroup(history.currentGroup))
    : null;
  const isGuestNow = !currentMembership;
  const nextMembership = isGuestNow
    ? [...memberships].sort(byValidFromAsc).find(m => isoDate(m.validFrom) > today) ?? null
    : null;
  const switchTargets = selectable.filter(g => !currentMembership || g.id !== Number(currentMembership.participantGroupId));
  const timeline = useMemo(() => buildTimeline(memberships), [memberships]);

  // ── Dialog state ──────────────────────────────────────────────────────────────────────────────
  const dialogOpen = !!dialog && isPopupOpen && popupData.name === POPUP[dialog.kind];
  const futureRelevant = dialog ? isFutureRelevant(dialog, today) : false;

  const previewBody = useMemo(() => {
    if (!dialogOpen || !player?.id) return null;
    const { kind, membership: m, form: f } = dialog;
    const base = { playerId: player.id };
    switch (kind) {
      case 'switch':
        return f.participantGroupId
          ? { ...base, participantGroupId: f.participantGroupId, validFrom: today, validTo: f.validTo || null, action: 'switch' }
          : null;
      case 'add':
        return f.participantGroupId && f.validFrom
          ? { ...base, participantGroupId: f.participantGroupId, validFrom: f.validFrom, validTo: f.validTo || null, action: 'add' }
          : null;
      case 'edit':
        return futureRelevant && f.validFrom
          ? { ...base, participantGroupId: m.participantGroupId, validFrom: f.validFrom, validTo: f.validTo || null, membershipId: m.id, action: 'edit' }
          : null;
      case 'extend':
        return futureRelevant
          ? { ...base, participantGroupId: m.participantGroupId, validFrom: isoDate(m.validFrom), validTo: extendTarget(m), membershipId: m.id, action: 'edit' }
          : null;
      // After ending/removing the player falls back to guest → price changes against the guest tariff.
      case 'end':
        return futureRelevant && f.date
          ? { ...base, participantGroupId: guest?.id ?? null, validFrom: isoDate(m.validFrom), validTo: f.date, membershipId: m.id, action: 'end' }
          : null;
      case 'remove':
        return futureRelevant
          ? { ...base, participantGroupId: guest?.id ?? null, membershipId: m.id, action: 'remove' }
          : null;
      default:
        return null;
    }
  }, [dialogOpen, dialog, player?.id, today, futureRelevant, guest?.id]);

  const preview = usePreviewRequest(PREVIEW_URL, previewBody);

  const setForm = (patch) => {
    setDialogError('');
    setDialog(d => (d ? { ...d, form: { ...d.form, ...patch } } : d));
  };

  const open = (kind, membership, form) => {
    setDialog({ kind, membership: membership ?? null, form });
    setDialogError('');
    openPopup(POPUP[kind], player.id);
  };

  const openSwitch = () => {
    const target = switchTargets[0] ?? null;
    open('switch', null, { participantGroupId: target?.id ?? null, validTo: defaultValidTo(target, today), apply: true, welcome: null });
  };

  // Suggests the range right after the latest known membership (e.g. next calendar year).
  const suggestRange = (group) => {
    const coveredUntil = memberships.reduce((max, m) => (m.validTo && isoDate(m.validTo) > max ? isoDate(m.validTo) : max), '');
    if (group?.validityMode === 'CalendarYear') {
      const yearStart = `${yearOf(today)}-01-01`;
      const from = coveredUntil >= yearStart ? addDays(coveredUntil, 1) : yearStart;
      return { from, to: endOfYear(yearOf(from)) };
    }
    return { from: coveredUntil >= today ? addDays(coveredUntil, 1) : today, to: '' };
  };

  const openAdd = () => {
    const group = selectable.find(g => g.id === currentGroup?.id)
      ?? selectable[0] ?? null;
    const { from, to } = suggestRange(group);
    open('add', null, {
      participantGroupId: group?.id ?? null, validFrom: from, validTo: to, toTouched: false,
      note: '', apply: true, welcome: null,
    });
  };

  const openEdit = (m) => open('edit', m, {
    validFrom: isoDate(m.validFrom), validTo: isoDate(m.validTo) ?? '', note: m.note ?? '', apply: true,
  });

  const openExtend = (m) => open('extend', m, { apply: true });

  // Default last day = yesterday → guest from today.
  const openEnd = (m) => {
    let date = addDays(today, -1);
    if (date < isoDate(m.validFrom)) date = isoDate(m.validFrom);
    if (m.validTo && date >= isoDate(m.validTo)) date = addDays(m.validTo, -1);
    open('end', m, { date, apply: true });
  };

  const openRemove = (m) => open('remove', m, { apply: true });

  // ── Form helpers ──────────────────────────────────────────────────────────────────────────────
  const changeSwitchGroup = (id) => {
    const group = byId.get(id);
    setForm({ participantGroupId: id, validTo: defaultValidTo(group, today), welcome: null });
  };

  const changeAddGroup = (id) => {
    const group = byId.get(id);
    const f = dialog.form;
    setForm({ participantGroupId: id, welcome: null, ...(f.toTouched ? {} : { validTo: defaultValidTo(group, f.validFrom || today) }) });
  };

  const changeAddFrom = (value) => {
    const f = dialog.form;
    const group = byId.get(f.participantGroupId);
    setForm({ validFrom: value, ...(f.toTouched || !value ? {} : { validTo: defaultValidTo(group, value) }) });
  };

  // Existing membership that a new/edited range would overlap (one group per player at a time).
  const overlapping = (() => {
    if (!dialog || (dialog.kind !== 'add' && dialog.kind !== 'edit') || !dialog.form.validFrom) return null;
    const { form: f, membership: m } = dialog;
    return memberships.find(x => (dialog.kind !== 'edit' || x.id !== m.id)
      && rangesOverlap(f.validFrom, f.validTo || null, x.validFrom, x.validTo)) ?? null;
  })();

  const overlapText = (x) => `Der Zeitraum überschneidet sich mit „${groupOf(x.participantGroupId, x.participantGroupName).name}“ `
    + `(${formatDate(x.validFrom)} – ${x.validTo ? formatDate(x.validTo) : 'unbefristet'}). Ein Spieler ist immer nur in einer Gruppe.`;

  const validate = () => {
    const { kind, membership: m, form: f } = dialog;
    switch (kind) {
      case 'switch':
        if (!f.participantGroupId) return 'Bitte eine Gruppe wählen.';
        if (f.validTo && f.validTo < today) return 'Das Bis-Datum darf nicht vor heute liegen.';
        return null;
      case 'add':
      case 'edit':
        if (kind === 'add' && !f.participantGroupId) return 'Bitte eine Gruppe wählen.';
        if (!f.validFrom) return 'Bitte ein Von-Datum angeben.';
        if (f.validTo && f.validTo < f.validFrom) return 'Das Bis-Datum darf nicht vor dem Von-Datum liegen.';
        if (overlapping) return overlapText(overlapping);
        return null;
      case 'end':
        if (!f.date) return 'Bitte ein Datum angeben.';
        if (f.date < isoDate(m.validFrom)) return 'Das Ende darf nicht vor dem Beginn liegen.';
        if (m.validTo && f.date >= isoDate(m.validTo)) return 'Die Mitgliedschaft endet bereits zu diesem Datum oder früher.';
        return null;
      default:
        return null;
    }
  };

  const submit = () => {
    if (saving || !dialog) return;
    const error = validate();
    if (error) {
      setDialogError(error);
      return;
    }
    const { kind, membership: m, form: f } = dialog;
    const apply = futureRelevant && !!f.apply;
    const base = '/ParticipantGroupMembership';
    let request;
    let messages;
    switch (kind) {
      case 'switch':
        request = () => api.post(`${base}/Switch`, {
          playerId: player.id, participantGroupId: f.participantGroupId, validTo: f.validTo || null,
          applyForFutureEvents: apply, sendWelcomeMail: f.welcome,
        });
        messages = isGuestNow ? ['Gruppe wird zugewiesen', 'Gruppe zugewiesen'] : ['Gruppe wird gewechselt', 'Gruppe gewechselt'];
        break;
      case 'add':
        request = () => api.post(`${base}/Add`, {
          playerId: player.id, participantGroupId: f.participantGroupId, validFrom: f.validFrom,
          validTo: f.validTo || null, note: f.note.trim() || null, applyForFutureEvents: apply, sendWelcomeMail: f.welcome,
        });
        messages = ['Mitgliedschaft wird hinzugefügt', 'Mitgliedschaft hinzugefügt'];
        break;
      case 'edit':
        request = () => api.put(`${base}/Edit/${m.id}`, {
          validFrom: f.validFrom, validTo: f.validTo || null, note: f.note.trim() || null, applyForFutureEvents: apply,
        });
        messages = ['Mitgliedschaft wird gespeichert', 'Mitgliedschaft gespeichert'];
        break;
      case 'extend':
        request = () => api.post(`${base}/Extend/${m.id}`);
        messages = ['Mitgliedschaft wird verlängert', 'Um ein Jahr verlängert'];
        break;
      case 'end':
        request = () => api.post(`${base}/End/${m.id}`, { date: f.date, applyForFutureEvents: apply });
        messages = ['Mitgliedschaft wird beendet', 'Mitgliedschaft beendet'];
        break;
      case 'remove':
        request = () => api.delete(`${base}/Remove/${m.id}?applyForFutureEvents=${apply}`);
        messages = ['Mitgliedschaft wird gelöscht', 'Mitgliedschaft gelöscht'];
        break;
      default:
        return;
    }

    setSaving(true);
    setDialogError('');
    startSuccessBarLoading(messages[0]);
    request()
      .then(() => {
        startSuccessBarSuccess(messages[1]);
        closePopup();
        onChanged();
      })
      .catch(err => {
        closeSuccessBar();
        setDialogError(apiErrorText(err, 'Die Änderung konnte nicht gespeichert werden.'));
      })
      .finally(() => setSaving(false));
  };

  // ── Popup building blocks ─────────────────────────────────────────────────────────────────────
  const previewData = preview.data;
  const futureLine = (opts) => futureParticipationsLine(previewData, { apply: dialog?.form.apply, ...opts });

  const renderApplyToggle = () => (futureRelevant ? (
    <CustomTogglebox
      placeholder={'Für zukünftige Spieltage übernehmen'}
      checked={!!dialog.form.apply}
      onChange={e => setForm({ apply: e.target.checked })}
    />
  ) : null);

  // Untouched (welcome === null) sends null → the group's mail rule decides; the toggle shows that decision.
  const renderWelcome = (groupName) => {
    if (!player.email) {
      return <p className={'formFieldHint'}>Keine E-Mail-Adresse hinterlegt – es wird keine Willkommensmail versendet.</p>;
    }
    const f = dialog.form;
    let hint = null;
    if (previewData && f.welcome === null) {
      hint = `Vorgabe aus den Mail-Regeln von „${previewData.welcomeGroupName ?? groupName}“: ${previewData.welcomeMail ? 'senden' : 'nicht senden'}.`;
    } else if (previewData && f.welcome !== null && f.welcome !== !!previewData.welcomeMail) {
      hint = 'Abweichend von der Vorgabe der Gruppe.';
    }
    return (
      <React.Fragment>
        <CustomTogglebox
          placeholder={'Willkommensmail senden'}
          checked={f.welcome ?? !!previewData?.welcomeMail}
          onChange={e => setForm({ welcome: e.target.checked })}
        />
        {hint && <p className={'formFieldHint'}>{hint}</p>}
      </React.Fragment>
    );
  };

  const renderImpact = (lines) => (
    <ImpactPreview
      lines={lines}
      warnings={previewBody ? (previewData?.warnings ?? []) : []}
      loading={!!previewBody && preview.loading}
      failed={!!previewBody && preview.failed}
    />
  );

  const renderError = () => (dialogError ? <p className={'editPlayerPopupError'}>{dialogError}</p> : null);

  const groupOptions = (groups) => groups.map(g => (
    <option key={g.id} value={g.id}>{g.name}</option>
  ));

  const renderSwitchBody = () => {
    const f = dialog.form;
    const target = byId.get(f.participantGroupId) ?? null;
    const endsOn = previewData?.endsCurrentMembershipOn;
    const plannedDeleted = memberships.filter(x => !x.isCurrent && isoDate(x.validFrom) >= today
      && (!f.validTo || isoDate(x.validFrom) <= f.validTo));
    const lines = [
      currentMembership && endsOn && (endsOn === 'wird gelöscht'
        ? `Die heute begonnene Mitgliedschaft „${groupOf(currentMembership.participantGroupId, currentMembership.participantGroupName).name}“ wird gelöscht.`
        : `Die Mitgliedschaft „${groupOf(currentMembership.participantGroupId, currentMembership.participantGroupName).name}“ endet am ${endsOn}.`),
      target && `${playerName || 'Der Spieler'} ist ab heute ${target.name}${f.validTo ? ` bis ${formatDate(f.validTo)}` : ' (unbefristet)'}.`,
      ...plannedDeleted.map(x => `Die geplante Mitgliedschaft „${groupOf(x.participantGroupId, x.participantGroupName).name}“ (${rangeLabel(x.validFrom, x.validTo, today)}) wird gelöscht.`),
      futureLine(),
    ];
    return (
      <React.Fragment>
        <p className={'editPlayerPopupSubtitle'}>{playerName} · aktuell {currentGroup?.name ?? '–'}</p>
        {renderError()}
        <h3>Neue Gruppe</h3>
        <CustomDropdown icon={'groups'} value={String(f.participantGroupId ?? '')} onChange={e => changeSwitchGroup(Number(e.target.value))}>
          {groupOptions(switchTargets)}
        </CustomDropdown>
        <h3>Gültigkeit</h3>
        <div className={'formFieldWrapper'}>
          <label className={'formFieldLabel'}>Gültig bis</label>
          <input type="date" min={today} value={f.validTo} onChange={e => setForm({ validTo: e.target.value })} />
        </div>
        <p className={'formFieldHint'}>
          Gilt ab heute ({formatDate(today)}). Leer = unbefristet.
          {target ? ` Gültigkeit der Gruppe: ${VALIDITY_MODE_LABEL[target.validityMode] ?? '–'}.` : ''}
        </p>
        {renderApplyToggle()}
        {renderWelcome(target?.name)}
        {renderImpact(lines)}
      </React.Fragment>
    );
  };

  const renderRangeFields = (f, onFromChange, onToChange) => (
    <div className={'formFieldRow'}>
      <div className={'formFieldWrapper'}>
        <label className={'formFieldLabel'}>Von</label>
        <input type="date" required value={f.validFrom} onChange={e => onFromChange(e.target.value)} />
      </div>
      <div className={'formFieldWrapper'}>
        <label className={'formFieldLabel'}>Bis</label>
        <input type="date" min={f.validFrom || undefined} value={f.validTo} onChange={e => onToChange(e.target.value)} />
      </div>
    </div>
  );

  const renderNoteField = (f) => (
    <div className={'formFieldWrapper'}>
      <label className={'formFieldLabel'}>Notiz (optional)</label>
      <input type="text" maxLength={NOTE_MAX_LENGTH} value={f.note} placeholder={'z. B. Beitrag bezahlt'}
        onChange={e => setForm({ note: e.target.value })} />
    </div>
  );

  const renderAddBody = () => {
    const f = dialog.form;
    const group = byId.get(f.participantGroupId) ?? null;
    const lines = [
      group && f.validFrom && `Neue Mitgliedschaft „${group.name}“ ${f.validTo ? `${formatDate(f.validFrom)} – ${formatDate(f.validTo)}` : `ab ${formatDate(f.validFrom)} (unbefristet)`}.`,
      futureRelevant ? futureLine() : 'Der Zeitraum liegt in der Vergangenheit – zukünftige Spieltage ändern sich nicht.',
    ];
    return (
      <React.Fragment>
        <p className={'editPlayerPopupSubtitle'}>{playerName}</p>
        {renderError()}
        <h3>Gruppe</h3>
        <CustomDropdown icon={'groups'} value={String(f.participantGroupId ?? '')} onChange={e => changeAddGroup(Number(e.target.value))}>
          {groupOptions(selectable)}
        </CustomDropdown>
        <h3>Zeitraum</h3>
        {renderRangeFields(f, changeAddFrom, value => setForm({ validTo: value, toTouched: true }))}
        <p className={'formFieldHint'}>
          Bis leer = unbefristet.{group ? ` Gültigkeit der Gruppe: ${VALIDITY_MODE_LABEL[group.validityMode] ?? '–'}.` : ''}
        </p>
        {overlapping && !dialogError && <p className={'editPlayerPopupError'}>{overlapText(overlapping)}</p>}
        {renderNoteField(f)}
        {renderApplyToggle()}
        {renderWelcome(group?.name)}
        {renderImpact(lines)}
      </React.Fragment>
    );
  };

  const renderMembershipHeader = (m) => {
    const group = groupOf(m.participantGroupId, m.participantGroupName);
    return (
      <div className={'playerGroupPopupMembership'}>
        <ParticipantGroupChip group={group} />
        <span className={'playerGroupPopupRange'}>{rangeLabel(m.validFrom, m.validTo, today)}</span>
      </div>
    );
  };

  const renderEditBody = () => {
    const { membership: m, form: f } = dialog;
    const lines = [futureRelevant ? futureLine() : 'Alter und neuer Zeitraum liegen in der Vergangenheit – zukünftige Spieltage ändern sich nicht.'];
    return (
      <React.Fragment>
        <p className={'editPlayerPopupSubtitle'}>{playerName}</p>
        {renderError()}
        {renderMembershipHeader(m)}
        <h3>Zeitraum</h3>
        {renderRangeFields(f, value => setForm({ validFrom: value }), value => setForm({ validTo: value }))}
        <p className={'formFieldHint'}>Bis leer = unbefristet. Die Gruppe selbst lässt sich nicht ändern – dafür „Gruppe wechseln“ nutzen.</p>
        {overlapping && !dialogError && <p className={'editPlayerPopupError'}>{overlapText(overlapping)}</p>}
        {renderNoteField(f)}
        {renderApplyToggle()}
        {renderImpact(lines)}
      </React.Fragment>
    );
  };

  const renderExtendBody = () => {
    const { membership: m } = dialog;
    const lines = [
      `Bis-Datum: ${formatDate(m.validTo)} → ${formatDate(extendTarget(m))}.`,
      futureRelevant ? futureLine({ apply: true }) : null,
    ];
    return (
      <React.Fragment>
        <p className={'editPlayerPopupSubtitle'}>{playerName}</p>
        {renderError()}
        {renderMembershipHeader(m)}
        <p className={'formFieldHint'}>Zukünftige Spieltage des Spielers werden beim Verlängern immer neu berechnet. Es wird keine Willkommensmail versendet.</p>
        {renderImpact(lines)}
      </React.Fragment>
    );
  };

  const renderEndBody = () => {
    const { membership: m, form: f } = dialog;
    const group = groupOf(m.participantGroupId, m.participantGroupName);
    const maxDate = m.validTo ? addDays(m.validTo, -1) : undefined;
    const lines = [
      f.date && `Letzter Tag als ${group.name}: ${formatDate(f.date)} – ab ${formatDate(addDays(f.date, 1))} ${guest?.name ?? 'Gast'} (sofern keine weitere Mitgliedschaft folgt).`,
      futureRelevant ? futureLine() : null,
    ];
    return (
      <React.Fragment>
        <p className={'editPlayerPopupSubtitle'}>{playerName}</p>
        {renderError()}
        {renderMembershipHeader(m)}
        <div className={'formFieldWrapper'}>
          <label className={'formFieldLabel'}>Endet am (letzter Tag)</label>
          <input type="date" required min={isoDate(m.validFrom)} max={maxDate} value={f.date} onChange={e => setForm({ date: e.target.value })} />
        </div>
        {renderApplyToggle()}
        {renderImpact(lines)}
      </React.Fragment>
    );
  };

  const renderRemoveBody = () => {
    const { membership: m } = dialog;
    const lines = [futureRelevant ? futureLine() : 'Der Zeitraum liegt in der Vergangenheit – zukünftige Spieltage ändern sich nicht.'];
    return (
      <React.Fragment>
        <p className={'editPlayerPopupSubtitle'}>{playerName}</p>
        {renderError()}
        {renderMembershipHeader(m)}
        <p>Soll diese Mitgliedschaft dauerhaft gelöscht werden?</p>
        <p className={'formFieldHint'}>Vergangene Spieltage behalten die Gruppe, die bei der Anmeldung gespeichert wurde.</p>
        {renderApplyToggle()}
        {renderImpact(lines)}
      </React.Fragment>
    );
  };

  // ── Section ───────────────────────────────────────────────────────────────────────────────────
  const renderCard = () => {
    if (!currentGroup) return null;
    const origin = currentMembership ? MEMBERSHIP_ORIGIN_LABEL[normalizeMembershipOrigin(currentMembership.origin)] : null;
    let meta;
    if (currentMembership) {
      meta = [
        `${isoDate(currentMembership.validFrom) <= today ? 'seit' : 'ab'} ${formatDate(currentMembership.validFrom)}`,
        currentMembership.validTo ? `bis ${formatDate(currentMembership.validTo)}` : 'unbefristet',
        origin,
      ].filter(Boolean).join(' · ');
    } else if (nextMembership) {
      meta = `Keine aktuelle Mitgliedschaft · ab ${formatDate(nextMembership.validFrom)}: ${groupOf(nextMembership.participantGroupId, nextMembership.participantGroupName).name}`;
    } else {
      meta = 'Keine Mitgliedschaft – ohne Gruppe ist ein Spieler Gast.';
    }
    return (
      <div className={'playerGroupCard'} style={{ '--player-group-color': colorVar(currentGroup.colorKey) }}>
        <ParticipantGroupDot participantGroup={currentGroup} size={'md'} withTooltip={false} />
        <div className={'playerGroupCardText'}>
          <span className={'playerGroupCardName'}>{currentGroup.name}</span>
          <span className={'playerGroupCardMeta'}>{meta}</span>
        </div>
        {switchTargets.length > 0 && (
          <ActionButton
            icon={isGuestNow ? 'group_add' : 'swap_horiz'}
            label={isGuestNow ? 'Gruppe zuweisen' : 'Gruppe wechseln'}
            type={3}
            onClick={openSwitch}
          />
        )}
      </div>
    );
  };

  const renderMembershipItem = (m) => {
    const group = groupOf(m.participantGroupId, m.participantGroupName);
    const colorKey = normalizeColorKey(group.colorKey);
    const isFuture = isoDate(m.validFrom) > today;
    const canExtend = group.validityMode === 'CalendarYear' && !!m.validTo;
    const canEnd = coversDate(m.validFrom, m.validTo, today) && (!m.validTo || isoDate(m.validTo) > today);
    const origin = MEMBERSHIP_ORIGIN_LABEL[normalizeMembershipOrigin(m.origin)];
    return (
      <li
        className={`playerGroupTimelineItem${m.isCurrent ? ' isCurrent' : ''}`}
        style={{ '--timeline-color': colorVar(colorKey) }}
      >
        <span className={`playerGroupTimelineMarker${colorKey !== 'neutral' ? ' isFilled' : ''}`} aria-hidden="true" />
        <div className={'playerGroupTimelineBody'}>
          <div className={'playerGroupTimelineHead'}>
            <span className={'playerGroupTimelineName'}>{group.name}</span>
            {m.isCurrent && <span className={'playerGroupTimelineBadge isCurrent'}>Aktuell</span>}
            {isFuture && <span className={'playerGroupTimelineBadge'}>Geplant</span>}
            {group.isArchived && <span className={'playerGroupTimelineBadge'}>Gruppe archiviert</span>}
          </div>
          <span className={'playerGroupTimelineMeta'}>
            {m.validTo ? `${formatDate(m.validFrom)} – ${formatDate(m.validTo)}` : `${rangeLabel(m.validFrom, null, today)} · unbefristet`}
            {origin ? ` · ${origin}` : ''}
          </span>
          {m.note && <span className={'playerGroupTimelineNote'}>{m.note}</span>}
          <div className={'playerGroupTimelineActions'}>
            <ActionButton icon={'edit'} label={'Bearbeiten'} onClick={() => openEdit(m)} />
            {canExtend && <ActionButton icon={'event_repeat'} label={'Verlängern'} onClick={() => openExtend(m)} />}
            {canEnd && <ActionButton icon={'event_busy'} label={'Beenden'} type={4} onClick={() => openEnd(m)} />}
            <ActionButton icon={'delete'} label={'Löschen'} type={1} onClick={() => openRemove(m)} />
          </div>
        </div>
      </li>
    );
  };

  const renderGuestItem = (item) => {
    const isCurrent = coversDate(item.from, item.to, today);
    const isFuture = isoDate(item.from) > today;
    return (
      <li className={`playerGroupTimelineItem isGuest${isCurrent ? ' isCurrent' : ''}`}>
        <span className={'playerGroupTimelineMarker'} aria-hidden="true" />
        <div className={'playerGroupTimelineBody'}>
          <div className={'playerGroupTimelineHead'}>
            <span className={'playerGroupTimelineName'}>{guest?.name ?? 'Gast'}</span>
            {isCurrent && <span className={'playerGroupTimelineBadge isCurrent'}>Aktuell</span>}
            {isFuture && <span className={'playerGroupTimelineBadge'}>Geplant</span>}
          </div>
          <span className={'playerGroupTimelineMeta'}>{rangeLabel(item.from, item.to, today)} · keine Mitgliedschaft</span>
        </div>
      </li>
    );
  };

  let body;
  if (historyFailed) {
    body = <p className={'playerGroupMessage'}>Gruppe und Verlauf konnten nicht geladen werden.</p>;
  } else if (!history) {
    body = <p className={'playerGroupMessage'}>Lädt …</p>;
  } else {
    body = (
      <React.Fragment>
        {renderCard()}
        <CustomTogglebox
          placeholder={'Gruppen-Ankündigungen per Mail erhalten'}
          checked={!!groupMailsEnabled}
          onChange={onGroupMailsChange}
        />
        <div className={'playerGroupTimelineHeader'}>
          <span className={'playerGroupSubtitle'}>Verlauf</span>
          {selectable.length > 0 && (
            <ActionButton icon={'add'} label={'Mitgliedschaft hinzufügen'} onClick={openAdd} />
          )}
        </div>
        {timeline.length === 0 ? (
          <p className={'playerGroupMessage'}>Noch keine Mitgliedschaft erfasst – der Spieler ist {guest?.name ?? 'Gast'}.</p>
        ) : (
          <ol className={'playerGroupTimeline'}>
            {timeline.map(item => (
              <React.Fragment key={item.key}>
                {item.kind === 'membership' ? renderMembershipItem(item.membership) : renderGuestItem(item)}
              </React.Fragment>
            ))}
          </ol>
        )}
      </React.Fragment>
    );
  }

  const confirmText = (label) => (saving ? 'Speichert …' : label);
  const popupProps = { withCancel: true, dontCloseOnConfirm: true, wide: true, onConfirm: submit };

  return (
    <section className={'playerGroupSection'} aria-label={'Gruppe'}>
      <label className={'innerContainerLabel'}>Gruppe</label>
      {body}

      <Popup name={POPUP.switch} headerText={isGuestNow ? 'Gruppe zuweisen' : 'Gruppe wechseln'}
        confirmText={confirmText(isGuestNow ? 'Zuweisen' : 'Wechseln')} type={'info'} {...popupProps}>
        {dialog?.kind === 'switch' && renderSwitchBody()}
      </Popup>
      <Popup name={POPUP.add} headerText={'Mitgliedschaft hinzufügen'}
        confirmText={confirmText('Hinzufügen')} type={'info'} {...popupProps}>
        {dialog?.kind === 'add' && renderAddBody()}
      </Popup>
      <Popup name={POPUP.edit} headerText={'Mitgliedschaft bearbeiten'}
        confirmText={confirmText('Speichern')} type={'info'} {...popupProps}>
        {dialog?.kind === 'edit' && renderEditBody()}
      </Popup>
      <Popup name={POPUP.extend} headerText={'Mitgliedschaft verlängern'}
        confirmText={confirmText('+1 Jahr')} type={'info'} {...popupProps}>
        {dialog?.kind === 'extend' && renderExtendBody()}
      </Popup>
      <Popup name={POPUP.end} headerText={'Mitgliedschaft beenden'}
        confirmText={confirmText('Beenden')} type={'warning'} {...popupProps}>
        {dialog?.kind === 'end' && renderEndBody()}
      </Popup>
      <Popup name={POPUP.remove} headerText={'Mitgliedschaft löschen'}
        confirmText={confirmText('Löschen')} type={'confirmaction'} {...popupProps}>
        {dialog?.kind === 'remove' && renderRemoveBody()}
      </Popup>
    </section>
  );
}

export default PlayerGroupSection;
